import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Manages the session list, pagination, search and elapsed-time updates.
 */
const useChatSessions = ({ apiCHAT, tokenChat, isMountedRef }) => {
    const [chatSessions, setChatSessions] = useState([]);
    const [myStatusCount, setMyStatusCount] = useState([]);
    const [generalStatusCount, setGeneralStatusCount] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchStatus, setSearchStatus] = useState('');

    const timeUpdateIntervalRef = useRef(null);
    const statusTimeoutMapRef = useRef(new Map());

    // ─── Elapsed time ────────────────────────────────────────────────────────

    const getElapsedTime = useCallback((timestamp) => {
        if (!timestamp) return '00:00';

        const now = new Date();
        const brazilTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
        const messageTime = new Date(timestamp);

        let diffMs = brazilTime - messageTime;
        if (diffMs < 0) return '00:00';

        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) return days === 1 ? '1 day' : `${days} days`;

        const formattedHours = Math.max(0, hours).toString().padStart(2, '0');
        const formattedMinutes = Math.max(0, minutes).toString().padStart(2, '0');
        return `${formattedHours}:${formattedMinutes}`;
    }, []);

    // ─── Session list helpers ─────────────────────────────────────────────────

    const updateSessionInList = useCallback((sessionId, newStatus, lastMessage = null) => {
        setChatSessions((prevSessions) =>
            prevSessions.map((session) => {
                if (session.id !== sessionId) return session;

                const updatedSession = { ...session, status: newStatus };

                if (lastMessage?.mensagem_usuario) {
                    updatedSession.last_message = {
                        ...session.last_message,
                        mensagem_usuario: lastMessage.mensagem_usuario,
                        data_mensagem: lastMessage.data_mensagem || new Date().toISOString(),
                    };
                }

                if (lastMessage?.texto) {
                    updatedSession.last_message = {
                        ...session.last_message,
                        mensagem_usuario: lastMessage.texto,
                        data_mensagem: lastMessage.timestamp || new Date().toISOString(),
                    };
                }

                return updatedSession;
            }),
        );
    }, []);

    // ─── Real-time time updates ───────────────────────────────────────────────

    const startTimeUpdates = useCallback(() => {
        if (timeUpdateIntervalRef.current) clearInterval(timeUpdateIntervalRef.current);

        timeUpdateIntervalRef.current = setInterval(() => {
            if (!isMountedRef.current) return;
            setChatSessions((prev) => [...prev]);
        }, 4000);
    }, [isMountedRef]);

    const stopTimeUpdates = useCallback(() => {
        if (timeUpdateIntervalRef.current) {
            clearInterval(timeUpdateIntervalRef.current);
            timeUpdateIntervalRef.current = null;
        }
    }, []);

    useEffect(() => {
        startTimeUpdates();
        return () => stopTimeUpdates();
    }, [startTimeUpdates, stopTimeUpdates]);

    // ─── Fetch sessions ───────────────────────────────────────────────────────

    const fetchChatSessions = useCallback(
        async (page = 1, chatToken = tokenChat, search = searchQuery, status = searchStatus) => {
            if (!chatToken || !isMountedRef.current) return;

            try {
                const params = {
                    page,
                    status,
                    ...(search ? { buscar: search } : {}),
                };

                const response = await apiCHAT.get('chat_sessions/paginate', {
                    params,
                    headers: { Authorization: `Bearer ${chatToken}` },
                });

                if (isMountedRef.current) {
                    setChatSessions((prevSessions) => {
                        return response.data.items.map((newSession) => {
                            const prevSession = prevSessions.find((prev) => prev.id === newSession.id);

                            if (prevSession && prevSession.last_message?.contar_nao_lida !== undefined) {
                                return {
                                    ...newSession,
                                    last_message: {
                                        ...newSession.last_message,
                                        contar_nao_lida: prevSession.last_message.contar_nao_lida,
                                    },
                                };
                            }

                            return newSession;
                        });
                    });

                    setCurrentPage(response.data.page);
                    setTotalPages(response.data.total_pages);
                    setMyStatusCount(response.data.my_status_counts);
                    setGeneralStatusCount(response.data.general_status_counts);
                }
            } catch (error) {
                console.error('Erro ao buscar sessões:', error);
            }
        },
        [apiCHAT, tokenChat, searchQuery, searchStatus, isMountedRef],
    );

    // ─── Debounced search ─────────────────────────────────────────────────────

    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchQuery !== undefined) {
                fetchChatSessions(1);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery, searchStatus]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        // State
        chatSessions,
        setChatSessions,
        myStatusCount,
        generalStatusCount,
        totalPages,
        currentPage,
        searchQuery,
        setSearchQuery,
        searchStatus,
        setSearchStatus,

        // Ref (needed by MQTT handler in parent)
        statusTimeoutMapRef,

        // Functions
        fetchChatSessions,
        getElapsedTime,
        updateSessionInList,
        startTimeUpdates,
        stopTimeUpdates,
    };
};

export default useChatSessions;
